import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from '@stylesheets/theme';
import useAuth from '@contexts/useAuth';
import useSteps from '@hooks/useSteps';
import usePrevious from '@hooks/usePrevious';
import { useSignupConfig } from '@hooks/useSignupConfig';
import { useSsoRouting } from '@hooks/useSsoRouting';
import { RETURN_TO_PARAM, rememberReturnTo, ssoLoginPath } from '@utils/ssoSession';
import { isPasswordComplexEnough } from '@utils/validation';
import { authForgotPassword, authConfirmPassword } from '@api/auth';
import LoginLayout from '@components/1-atoms/Layouts/LoginLayout/LoginLayout';
import Field from '@components/1-atoms/Field';
import Button, { BUTTON_VARIANT_TEXT } from '@components/1-atoms/Button';
import PageHeader, {
  PAGE_HEADER_VARIANT_LARGE,
} from '@components/2-molecules/PageHeader';
import LoginResetPasswordEmail from '@components/3-organisms/LoginResetPasswordEmail';
import LoginResetPassword from '@components/3-organisms/LoginResetPassword';
import {
  ErrorBox,
  SuccessBox,
  Divider,
  InheritLink,
} from '../auth.styled';
import {
  Header,
  Logo,
  BrandName,
  LoginForm,
  LoginStepContent,
  MfaForm,
  FooterLinks,
} from './Login.styled';
import { IS_OS_URL } from '@constants/index';

const LOGIN_STEP_USERNAME = 'username';
const LOGIN_STEP_RESET_PASSWORD_EMAIL = 'resetPasswordEmail';
const LOGIN_STEP_RESET_PASSWORD_NEW_PASSWORD = 'resetPasswordNewPassword';

const LOGIN_STEPS = [
  LOGIN_STEP_USERNAME,
  LOGIN_STEP_RESET_PASSWORD_EMAIL,
  LOGIN_STEP_RESET_PASSWORD_NEW_PASSWORD,
];

const Login: React.FC = () => {
  const { login, completeMfa, isLoading, error, camLoginUrl, clearError, mfaRequired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { signupBlocked } = useSignupConfig();
  const { ssoRoute, checkEmail, resetSsoRoute } = useSsoRouting();
  const verified = (location.state as { verified?: boolean })?.verified;

  useEffect(() => {
    rememberReturnTo(new URLSearchParams(location.search).get(RETURN_TO_PARAM));
  }, [location.search]);

  const [mfaCode, setMfaCode] = useState('');
  const [revealPassword, setRevealPassword] = useState(false);
  const [passwordResetFailed, setPasswordResetFailed] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const canAnimateSteps = useRef(false);

  const { goTo, isStepActive, currentStepName, getStepIndex } = useSteps({
    steps: LOGIN_STEPS,
    initialStepNumber: 0,
  });
  const previousCurrentStepName = usePrevious(currentStepName);

  useEffect(() => {
    canAnimateSteps.current = true;
  }, []);

  const { contextSafe } = useGSAP(
    () => {
      if (!canAnimateSteps.current) return;

      const isMovingBackwards =
        getStepIndex(currentStepName) < getStepIndex(previousCurrentStepName);

      const startFrom = isMovingBackwards ? '-100%' : '100%';

      gsap.from('.login-step', { duration: 0.15, x: startFrom, opacity: 0 });
    },
    {
      scope: formRef,
      dependencies: [currentStepName, getStepIndex, canAnimateSteps],
    },
  );

  const goToStep = contextSafe((step: string) => {
    const isMovingForward = getStepIndex(step) > getStepIndex(currentStepName);
    const startFrom = isMovingForward ? '-100%' : '100%';

    gsap.to('.login-step', {
      duration: 0.15,
      x: startFrom,
      opacity: 0,
      onComplete: () => goTo(step),
    });
  });

  const handleGoToStep = useCallback(
    (step: string) => () => goToStep(step),
    [goToStep],
  );

  const handleResetPasswordClick = useCallback(
    (resetForm: () => void) => () => {
      resetForm();
      setPasswordResetFailed(false);
      setForgotPasswordEmail(null);
      clearError();
      goToStep(LOGIN_STEP_RESET_PASSWORD_EMAIL);
    },
    [clearError, goToStep],
  );

  const navigateToLoginStep = useCallback(() => {
    setForgotPasswordEmail(null);
    setPasswordResetFailed(false);
    goToStep(LOGIN_STEP_USERNAME);
  }, [goToStep]);

  const toggleRevealPassword = useCallback(() => {
    setRevealPassword((prev) => !prev);
  }, []);

  const validationSchema = useMemo(() => {
    if (isStepActive(LOGIN_STEP_RESET_PASSWORD_EMAIL)) {
      return Yup.object({
        email: Yup.string()
          .required('Email cannot be empty')
          .email('Please enter a valid email address'),
      });
    }

    if (isStepActive(LOGIN_STEP_RESET_PASSWORD_NEW_PASSWORD)) {
      const schema = Yup.object({
        newPassword: Yup.string()
          .required('New password field cannot be empty')
          .test('password-complex-enough', 'Password must be at least 8 characters and include uppercase, lowercase and a number', (p) => isPasswordComplexEnough(p)),
        confirmNewPassword: Yup.string()
          .required('Confirm password field cannot be empty')
          .oneOf([Yup.ref('newPassword')], 'Confirm password must match new password'),
      });

      if (forgotPasswordEmail) {
        return schema.shape({
          code: Yup.string()
            .required('Verification code cannot be empty')
            .length(6, 'Verification code must be 6 digits'),
        });
      }

      return schema;
    }

    return Yup.object({
      email: Yup.string().email('Invalid email').required('Email is required'),
      password: ssoRoute ? Yup.string() : Yup.string().required('Password is required'),
    });
  }, [isStepActive, forgotPasswordEmail, ssoRoute]);

  const formInitialValues = useMemo(
    () => ({
      email: '',
      password: '',
      code: '',
      newPassword: '',
      confirmNewPassword: '',
    }),
    [],
  );

  const onFormSubmit = useCallback(
    async (
      values: Record<string, string>,
      {
        setSubmitting,
        setErrors,
        setFieldError,
      }: {
        setSubmitting: (s: boolean) => void;
        setErrors: (errors: Record<string, string>) => void;
        setFieldError: (field: string, message: string) => void;
      },
    ) => {
      setErrors({});

      if (isStepActive(LOGIN_STEP_RESET_PASSWORD_EMAIL)) {
        setPasswordResetFailed(false);
        try {
          await authForgotPassword(values.email);
          setForgotPasswordEmail(values.email);
          goToStep(LOGIN_STEP_RESET_PASSWORD_NEW_PASSWORD);
        } catch {
          setPasswordResetFailed(true);
        } finally {
          setSubmitting(false);
        }
        return;
      }

      if (isStepActive(LOGIN_STEP_RESET_PASSWORD_NEW_PASSWORD) && forgotPasswordEmail) {
        try {
          await authConfirmPassword(forgotPasswordEmail, values.code, values.newPassword);
          navigateToLoginStep();
        } catch (err) {
          setFieldError(
            'code',
            err instanceof Error ? err.message : 'Invalid or expired code. Please try again.',
          );
        } finally {
          setSubmitting(false);
        }
        return;
      }

      if (ssoRoute) {
        setSubmitting(false);
        navigate(ssoLoginPath(ssoRoute.provider));
        return;
      }

      clearError();
      try {
        await login(values.email, values.password);
      } finally {
        setSubmitting(false);
      }
    },
    [isStepActive, goToStep, navigateToLoginStep, forgotPasswordEmail, clearError, login, ssoRoute, navigate],
  );

  const handleMfaSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      clearError();
      try {
        await completeMfa(mfaCode);
      } catch {
        // error handled by context
      }
    },
    [completeMfa, mfaCode, clearError],
  );

  const handleMfaCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setMfaCode(e.target.value),
    [],
  );

  if (mfaRequired) {
    return (
      <ThemeProvider theme={darkTheme}>
        <LoginLayout>
          <Header>
            <Logo />
            <BrandName>Aibuild OS</BrandName>
          </Header>
          <LoginForm as="div">
            <LoginStepContent>
              <PageHeader
                variant={PAGE_HEADER_VARIANT_LARGE}
                title="Two-factor authentication"
                subtitle="Enter the code from your authenticator app."
              />

              {error && <ErrorBox>{error}</ErrorBox>}
              <MfaForm onSubmit={handleMfaSubmit}>
                <Field
                  type="text"
                  placeholder="6-digit code"
                  value={mfaCode}
                  onChange={handleMfaCodeChange}
                  maxLength={6}
                  autoFocus
                />
                <Button type="submit" disabled={isLoading || mfaCode.length < 6} loading={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify'}
                </Button>
              </MfaForm>
            </LoginStepContent>
          </LoginForm>
        </LoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <LoginLayout>
        <Header>
          <Logo />
          <BrandName>Aibuild OS</BrandName>
        </Header>

        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={onFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            resetForm,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <>
              <LoginForm ref={formRef} onSubmit={handleSubmit}>
                {isStepActive(LOGIN_STEP_USERNAME) && (
                    <LoginStepContent className="login-step">
                      <PageHeader
                        variant={PAGE_HEADER_VARIANT_LARGE}
                        title="Log in"
                        subtitle="Welcome back. Enter your credentials to continue."
                      />

                      {verified && (
                        <SuccessBox>Email verified successfully. You can now log in.</SuccessBox>
                      )}

                      {error && (
                        <ErrorBox>
                          {error}
                          {camLoginUrl && (
                            <>
                              {' '}
                              <InheritLink href={camLoginUrl}>
                                Go to Aibuild CAM login &rarr;
                              </InheritLink>
                            </>
                          )}
                        </ErrorBox>
                      )}

                      <Field
                        name="email"
                        type="email"
                        placeholder="Email address"
                        value={values.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleChange(e);
                          resetSsoRoute();
                        }}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                          handleBlur(e);
                          void checkEmail(e.target.value);
                        }}
                        error={!!(touched.email && errors.email)}
                        supportingText={
                          touched.email && errors.email ? errors.email : undefined
                        }
                      />

                      {ssoRoute ? (
                        <Button type="submit">Continue with {ssoRoute.providerName}</Button>
                      ) : (
                        <>
                          <Field
                            name="password"
                            type={revealPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!(touched.password && errors.password)}
                            supportingText={
                              touched.password && errors.password ? errors.password : undefined
                            }
                            endingButtonIconName={
                              revealPassword ? 'visibility_on_1' : 'visibility_off_0'
                            }
                            onEndingButtonClick={toggleRevealPassword}
                          />

                          <Button
                            type="submit"
                            disabled={isSubmitting || isLoading}
                            loading={isLoading}
                          >
                            Log in
                          </Button>
                        </>
                      )}

                      {IS_OS_URL && !signupBlocked && (
                        <>
                          <Divider>OR</Divider>
                          <Button
                            variant={BUTTON_VARIANT_TEXT}
                            onClick={() => navigate('/auth/signup')}
                          >
                            New here? Create an account
                          </Button>
                        </>
                      )}
                    </LoginStepContent>
                  )}

                  {isStepActive(LOGIN_STEP_RESET_PASSWORD_EMAIL) && (
                    <LoginResetPasswordEmail
                      className="login-step"
                      email={values.email}
                      emailError={!!errors.email}
                      emailErrorMessage={errors.email}
                      emailTouched={!!touched.email}
                      handleFieldBlur={handleBlur}
                      handleFieldChange={handleChange}
                      handleGoBack={handleGoToStep(LOGIN_STEP_USERNAME)}
                      isSubmitting={isSubmitting}
                      resetFailed={passwordResetFailed}
                    />
                  )}

                  {isStepActive(LOGIN_STEP_RESET_PASSWORD_NEW_PASSWORD) && (
                    <LoginResetPassword
                      className="login-step"
                      code={values.code}
                      codeError={!!errors.code}
                      codeErrorMessage={errors.code}
                      codeTouched={!!touched.code}
                      confirmNewPassword={values.confirmNewPassword}
                      confirmNewPasswordError={!!errors.confirmNewPassword}
                      confirmNewPasswordErrorMessage={errors.confirmNewPassword}
                      confirmNewPasswordTouched={!!touched.confirmNewPassword}
                      handleFieldBlur={handleBlur}
                      handleFieldChange={handleChange}
                      isSubmitting={isSubmitting}
                      newPassword={values.newPassword}
                      newPasswordError={!!errors.newPassword}
                      newPasswordErrorMessage={errors.newPassword}
                      newPasswordTouched={!!touched.newPassword}
                      resetPasswordButtonLabel="Reset Password"
                      showCodeField={!!forgotPasswordEmail}
                      subtitle={
                        forgotPasswordEmail
                          ? `We sent a 6-digit code to ${forgotPasswordEmail}. Enter it below along with your new password.`
                          : undefined
                      }
                    />
                  )}
              </LoginForm>

              {isStepActive(LOGIN_STEP_USERNAME) && !ssoRoute && (
                <FooterLinks>
                  <Button
                    variant={BUTTON_VARIANT_TEXT}
                    onClick={handleResetPasswordClick(resetForm)}
                  >
                    Forgot password?
                  </Button>
                </FooterLinks>
              )}
            </>
          )}
        </Formik>
      </LoginLayout>
    </ThemeProvider>
  );
};

export default Login;
